// apps/web/src/pages/admin/account-operations.ts — Quick-Task 260920-u3p.
//
// GraphQL operation constants for the /admin/einstellungen/konto
// page (admin self-service email-change). The operations here are
// registered in scripts/build-persisted-ops.ts so the production
// persisted-operations gate (D-09-03) accepts them.
//
// Per plan 02-04 + Quick-Task 260920-u3p: the operation strings live
// in a dedicated operations file (NOT inside the .tsx) so the page
// component imports just the documents it needs. The types mirror
// the API contract in apps/api/src/graphql/schema.ts.

import { gql } from "graphql-request";

/**
 * Current user's email + role. The /admin/einstellungen/konto page
 * renders the email as read-only so the admin can confirm the address
 * they're about to change FROM before they type the new one. The
 * `MeAccount` operation name is unique to this surface so the persisted-
 * ops gate can pin it without colliding with lib/auth.tsx's `MeAuth`.
 */
export const ME_ACCOUNT_QUERY = gql`
  query MeAccount {
    me {
      id
      email
      role
    }
  }
`;

/**
 * Request an admin email change. The mutation is RBAC-gated (admin
 * only) and rate-limited (3 requests / 24h per adminUserId). On
 * success the server enqueues a magic-link mail to the NEW address.
 *
 * Payload fields:
 *   pendingChangeId — the PendingEmailChange row id (worker uses it
 *                     to dispatch the magic-link).
 *   newEmail       — normalised (lowercased) email the admin claimed.
 *   expiresAt      — TTL the magic-link expires at (24h after request).
 *
 * Error codes the web client maps via i18next:
 *   INVALID_INPUT                   — server-side same-email guard
 *                                     (the UI also guards this client-
 *                                     side; the server guard exists
 *                                     so a misbehaving client cannot
 *                                     issue a no-op).
 *   EMAIL_TAKEN                     — cross-table collision guard
 *                                     (AdminUser.email OR
 *                                     PendingEmailChange.newEmail).
 *   ADMIN_EMAIL_CHANGE_RATE_LIMITED — 3/24h ceiling.
 *   CSRF_TOKEN_MISMATCH             — assertXsrf rejected the
 *                                     x-xsrf-token header.
 */
export const REQUEST_ADMIN_EMAIL_CHANGE_MUTATION = gql`
  mutation RequestAdminEmailChangeAccount($newEmail: String!) {
    requestAdminEmailChange(newEmail: $newEmail) {
      pendingChangeId
      newEmail
      expiresAt
    }
  }
`;
